import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Banknote, Bell, CheckCheck, ChevronRight, FileText } from 'lucide-react';
import { formatEuro } from '../../utils/currency';
import { appNotificationService, type AppNotification, type PaymentDetails } from '../../services/appNotificationService';
import { invoiceRequestService, type InvoiceRequest } from '../../services/invoiceRequestService';
import { useAuth } from '../../contexts/AuthContext';
import { useModal } from '../../contexts/ModalContext';
import { log } from '../../lib/logger';

const POLL_MS = 60_000;
const PAGE_SIZE = 30;

const STATUS_CHIP: Record<InvoiceRequest['status'], { label: string; className: string }> = {
  pending: { label: 'Por emitir', className: 'cm-chip cm-chip-warning' },
  done: { label: 'Concluído', className: 'cm-chip cm-chip-success' },
  cancelled: { label: 'Cancelado', className: 'cm-chip cm-chip-neutral' },
};

function timeAgo(iso: string): string {
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (minutes < 1) return 'agora';
  if (minutes < 60) return `há ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `há ${hours} h`;
  return new Date(iso).toLocaleDateString('pt-PT', { day: 'numeric', month: 'short' });
}

/** Newest first, under the headings a person thinks in. */
export function groupByDay(notifications: AppNotification[], now = new Date()): { label: string; items: AppNotification[] }[] {
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const weekAgo = startOfToday - 6 * 24 * 60 * 60 * 1000;
  const groups = [
    { label: 'Hoje', items: [] as AppNotification[] },
    { label: 'Últimos 7 dias', items: [] as AppNotification[] },
    { label: 'Mais antigas', items: [] as AppNotification[] },
  ];
  for (const n of notifications) {
    const at = new Date(n.created_at).getTime();
    groups[at >= startOfToday ? 0 : at >= weekAgo ? 1 : 2].items.push(n);
  }
  return groups.filter(g => g.items.length > 0);
}

/** What is still to come in: an Account chases the rest, or knows it's closed. */
export function openInvoicesLabel(details: PaymentDetails): string {
  const open = details.open_invoices;
  if (details.partial) return 'Esta fatura ainda não está paga na totalidade';
  if (open === 0) return 'Todas as faturas estão pagas';
  return open === 1 ? 'Falta receber 1 fatura' : `Faltam receber ${open} faturas`;
}

function NotificationIcon({ type }: { type: string }) {
  if (type === 'payment_received') {
    return (
      <span className="w-8 h-8 shrink-0 rounded-full grid place-items-center bg-emerald-100 text-emerald-700">
        <Banknote className="w-4 h-4" />
      </span>
    );
  }
  if (type === 'invoice_request') {
    return (
      <span className="w-8 h-8 shrink-0 rounded-full grid place-items-center bg-amber-100 text-amber-800">
        <FileText className="w-4 h-4" />
      </span>
    );
  }
  return (
    <span className="w-8 h-8 shrink-0 rounded-full grid place-items-center bg-gray-100 text-gray-600">
      <Bell className="w-4 h-4" />
    </span>
  );
}

function RequestDetails({ request }: { request: InvoiceRequest }) {
  return (
    <div className="text-xs text-gray-600 space-y-0.5">
      <p className="text-gray-800">{request.campaign_name}</p>
      <p>
        <strong className="text-gray-800">{request.client_name || 'Cliente sem nome'}</strong>
        {request.client_vat && <> · NIF {request.client_vat}</>}
      </p>
      {request.client_address && <p className="text-gray-500">{request.client_address}</p>}
      <p className="tabular-nums">
        {request.lines.map((l, i) => (
          <span key={l.id}>{i > 0 && ' + '}{formatEuro(l.amount)}{l.fulfilled_at && ' ✓'}</span>
        ))}
        {request.lines.length > 1 && <> = <strong>{formatEuro(request.total)}</strong></>}
        <span className="text-gray-400"> sem IVA</span>
      </p>
      {request.purchase_order && <p>Ordem de compra: {request.purchase_order}</p>}
      {request.notes && <p className="whitespace-pre-wrap bg-gray-50 rounded-lg px-2 py-1.5 text-gray-700">{request.notes}</p>}
    </div>
  );
}

function PaymentFigures({ notification, details }: { notification: AppNotification; details: PaymentDetails }) {
  return (
    <div className="text-xs text-gray-600 space-y-0.5">
      {notification.body && <p className="text-gray-800">{notification.body}</p>}
      <p className="tabular-nums">
        {details.invoice_number && <>Fatura {details.invoice_number} · </>}
        <strong className="text-gray-900">{formatEuro(Number(details.amount) || 0)}</strong>
        <span className="text-gray-400"> sem IVA</span>
      </p>
      <p className={details.open_invoices === 0 && !details.partial ? 'text-emerald-700' : 'text-gray-500'}>{openInvoicesLabel(details)}</p>
    </div>
  );
}

/** Everyone's notification centre in the main app. Lives in the header, on every page. */
export function NotificationBell({
  onOpenCampaign,
  onOpenProjecto,
}: {
  onOpenCampaign: (campaignId: string) => void;
  onOpenProjecto: (projectoId: string) => void;
}) {
  const { user } = useAuth();
  const { toast } = useModal();
  const userId = user?.id;
  const [open, setOpen] = useState(false);
  const [available, setAvailable] = useState(true);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [requests, setRequests] = useState<Map<string, InvoiceRequest>>(new Map());
  const rootRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    try {
      const list = await appNotificationService.list(limit);
      setNotifications(list);
      const ids = [...new Set(list.map(n => n.invoice_request_id).filter((id): id is string => !!id))];
      const found = await invoiceRequestService.listByIds(ids);
      setRequests(new Map(found.map(r => [r.id, r])));
      setAvailable(true);
    } catch (err) {
      // Tables not there yet (SQL not applied): no bell rather than an error
      log.warn('Notifications not loaded:', err);
      setAvailable(false);
    }
  }, [limit]);

  useEffect(() => {
    load();
    const timer = window.setInterval(load, POLL_MS);
    window.addEventListener('focus', load);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', load);
    };
  }, [load]);

  useEffect(() => {
    if (!userId) return;
    return appNotificationService.subscribeToNew(userId, notification => {
      load();
      if (notification.type === 'payment_received') toast.success(`${notification.title}: ${notification.body ?? ''}`.trim());
    });
    // toast is a new object on every render of the provider
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, load]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const unread = useMemo(() => notifications.filter(n => !n.read_at), [notifications]);
  const groups = useMemo(() => groupByDay(unreadOnly ? unread : notifications), [unreadOnly, unread, notifications]);

  if (!available) return null;

  const canLoadMore = notifications.length === limit;

  async function markRead(ids: string[]) {
    const now = new Date().toISOString();
    setNotifications(prev => prev.map(n => ids.includes(n.id) && !n.read_at ? { ...n, read_at: now } : n));
    try {
      await appNotificationService.markRead(ids);
    } catch (err) {
      log.warn('Could not mark notifications read:', err);
    }
  }

  function openNotification(n: AppNotification) {
    if (!n.read_at) markRead([n.id]);
    setOpen(false);
    if (n.projecto_id) onOpenProjecto(n.projecto_id);
    else if (n.campaign_id) onOpenCampaign(n.campaign_id);
  }

  return (
    <div ref={rootRef} className="relative z-40">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="relative flex items-center justify-center w-10 h-10 rounded-full bg-white shadow-card hover:shadow transition-shadow"
        aria-label={unread.length > 0 ? `Notificações, ${unread.length} por ler` : 'Notificações'}
        aria-expanded={open}
      >
        <Bell className="w-5 h-5 text-gray-700" />
        {unread.length > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center tabular-nums">
            {unread.length > 9 ? '9+' : unread.length}
          </span>
        )}
      </button>

      {open && (
        <div
          className="fixed inset-x-3 top-16 sm:absolute sm:inset-x-auto sm:top-auto sm:right-0 sm:mt-2 sm:w-[420px] bg-white rounded-2xl shadow-float ring-1 ring-gray-100 overflow-hidden flex flex-col max-h-[calc(100dvh-5rem)] sm:max-h-[75vh]"
          role="dialog"
          aria-label="Notificações"
        >
          <div className="px-4 pt-3 pb-2.5 border-b border-gray-100 space-y-2.5">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm font-bold text-brand-ink">Notificações</p>
              {unread.length > 0 && (
                <button type="button" onClick={() => markRead(unread.map(n => n.id))} className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-brand-ink whitespace-nowrap">
                  <CheckCheck className="w-3.5 h-3.5" />
                  Marcar todas como lidas
                </button>
              )}
            </div>
            <div className="flex gap-1.5" role="group" aria-label="Filtrar notificações">
              {[
                { value: false, label: 'Todas' },
                { value: true, label: unread.length > 0 ? `Por ler (${unread.length})` : 'Por ler' },
              ].map(option => (
                <button
                  key={option.label}
                  type="button"
                  aria-pressed={unreadOnly === option.value}
                  onClick={() => setUnreadOnly(option.value)}
                  className={`h-7 px-3 rounded-full text-xs font-medium transition-colors ${
                    unreadOnly === option.value ? 'bg-brand-ink text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="overflow-y-auto flex-1">
            {groups.length === 0 && (
              <div className="px-6 py-10 text-center">
                <Bell className="w-6 h-6 text-gray-300 mx-auto mb-2" />
                <p className="text-sm font-medium text-gray-700">{unreadOnly ? 'Está tudo lido' : 'Ainda sem notificações'}</p>
                {!unreadOnly && (
                  <p className="text-xs text-gray-500 mt-1">Vais ver aqui os pagamentos recebidos das tuas campanhas e os pedidos de fatura.</p>
                )}
              </div>
            )}
            {groups.map(group => (
              <section key={group.label}>
                <h3 className="sticky top-0 z-[1] px-4 py-1.5 bg-gray-50 text-[11px] font-semibold uppercase tracking-wide text-gray-500">{group.label}</h3>
                <div className="divide-y divide-gray-100">
                  {group.items.map(n => {
                    const request = n.invoice_request_id ? requests.get(n.invoice_request_id) : undefined;
                    const chip = request ? STATUS_CHIP[request.status] : null;
                    const canOpen = !!(n.campaign_id || n.projecto_id);
                    return (
                      <div key={n.id} className={`px-4 py-3 flex gap-3 ${n.read_at ? '' : 'bg-brand-lime/10'}`}>
                        <NotificationIcon type={n.type} />
                        <div className="min-w-0 flex-1 space-y-1">
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-sm font-semibold text-brand-ink">
                              {!n.read_at && <span className="inline-block w-2 h-2 rounded-full bg-red-500 mr-1.5 align-middle" aria-label="Por ler" />}
                              {n.title}
                            </p>
                            <span className="text-[11px] text-gray-400 whitespace-nowrap pt-0.5">{timeAgo(n.created_at)}</span>
                          </div>
                          {n.type === 'payment_received' && n.details ? (
                            <PaymentFigures notification={n} details={n.details} />
                          ) : request ? (
                            <RequestDetails request={request} />
                          ) : n.body ? (
                            <p className="text-xs text-gray-600">{n.body}</p>
                          ) : null}
                          {(chip || canOpen) && (
                            <div className="flex items-center justify-between gap-2 pt-0.5">
                              {chip ? <span className={chip.className}>{chip.label}</span> : <span />}
                              {canOpen && (
                                <button type="button" onClick={() => openNotification(n)} className="inline-flex items-center gap-0.5 -my-1 py-1 text-xs font-semibold text-brand-ink hover:underline">
                                  {n.projecto_id ? 'Abrir projeto' : 'Abrir campanha'}
                                  <ChevronRight className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}
            {canLoadMore && !unreadOnly && (
              <button type="button" onClick={() => setLimit(l => l + PAGE_SIZE)} className="w-full py-2.5 text-xs font-semibold text-gray-600 hover:text-brand-ink border-t border-gray-100">
                Ver mais antigas
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
